import { COLORS } from 'constants/constant';
import dayjs from 'dayjs';
import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  LabelProps,
  Legend,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { RevenueInterface } from '../types/revenueDashboardType';

type RevenueBarChartProps = {
  filteredBarChartData: RevenueInterface[];
  searchParams: URLSearchParams;
  selectedMonth: string;
  setSelectedMonth: (month: string) => void;
  hoverLegend: string | null;
  setHoverLegend: (legend: string | null) => void;
  selectedLegend: string | null;
  handleLegendClick: (data: Payload) => void;
  handleBarClick: (yearmon: string) => void;
};

const LegendList = [
  'video_rev',
  'display_rev',
  'sponsorship_rev',
  'distribution_rev',
  'theatrical_rev',
  'svod_rev',
  'ppv_rev',
];

export const RevenueBarChart: React.FC<RevenueBarChartProps> = ({
  filteredBarChartData,
  selectedMonth,
  hoverLegend,
  setHoverLegend,
  selectedLegend,
  handleLegendClick,
  handleBarClick,
}: RevenueBarChartProps) => {
  const renderCustomLabel = ({ x, y, value, width }: LabelProps): React.ReactNode => {
    if (typeof x === 'undefined' || typeof y === 'undefined' || typeof value === 'undefined') {
      return null;
    }

    return (
      <Text
        x={Number(x) + Number(width) / 2} // x 위치를 막대의 중앙으로 조정
        y={Number(y) - 10} // y 위치를 막대 위로 약간 띄움
        fill="#007bff"
        textAnchor="middle"
        fontSize={14}
        fontWeight="bold"
      >
        {`${(Number(value) / 1000000).toFixed(1)}M`}
      </Text>
    );
  };

  const getOpacity = (currentKey: string): number => {
    if (hoverLegend) {
      return hoverLegend === currentKey ? 1 : 0.3;
    }

    if (selectedLegend) {
      return selectedLegend === currentKey ? 1 : 0.3;
    }
    return 1;
  };

  return (
    <div style={{ width: '100%', height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={filteredBarChartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          onClick={e => e && e.activePayload && handleBarClick(e.activePayload[0].payload.yearmon)}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="yearmon"
            tickFormatter={(value: string) => dayjs(value).format('YYYY-MM')}
          />
          <YAxis
            domain={['auto', 'auto']}
            tickFormatter={(value: number) => `${(value / 1000000).toFixed(1)}M`}
          />
          <Tooltip />
          <Legend
            onClick={handleLegendClick}
            wrapperStyle={{
              cursor: 'pointer',
            }}
            onMouseEnter={e => {
              setHoverLegend(e.dataKey as string);
            }}
            onMouseLeave={() => {
              setHoverLegend(null);
            }}
            formatter={(value, entry: Payload) => (
              <span
                style={{
                  fontWeight: selectedMonth === entry.dataKey ? 'bold' : 'normal',
                  color: selectedMonth === entry.dataKey ? '#000' : '#666',
                }}
              >
                {value}
              </span>
            )}
          />
          {LegendList.map((legend, index) => (
            <Bar
              key={legend}
              dataKey={legend}
              stackId="a"
              fill={COLORS[index]}
              opacity={getOpacity(legend)}
            >
              {legend === LegendList[LegendList.length - 1] && (
                <LabelList dataKey="total_rev" content={renderCustomLabel} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
